import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchLoading } from '@shared/hooks/useSearchLoading';
import type { DateRange } from '@shared/components/FilterPopup/types';
import { listMyAssignedContactRequestsApiService } from '../api/listMyAssignedContactRequestsApiService';
import type { ContactRequestListItem } from '../types/contactRequestTypes';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

/**
 * State + data fetching for the admin "My Contact Requests" tab
 * (`GET /contact-requests/my-tasks`).
 *
 * The contact-request counterpart of `useMyAssignedTasksList`: the endpoint is
 * self-scoped to the caller's assigned requests, so the only filter is the
 * `created_at` date range (no status filter). Search matches on Contact Request
 * ID or Client Name. URL params use the page's camelCase convention
 * (`startDate`/`endDate`), which are also the names the API expects. The `tab`
 * and `cols` params are owned by other components and preserved. A 403 sets
 * `accessError` so the tab can render the access-denied screen.
 */

export interface UseMyAssignedContactRequestsListResult {
  items: ContactRequestListItem[];
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  totalCount: number;
  search: string;
  setSearch: (search: string) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  appliedFilters: { dateRange: DateRange };
  handleApplyFilters: () => void;
  handleClearFilters: () => void;
  limitError: string;
  handleLimitChange: (limit: number) => void;
  hasNoData: boolean;
  isSearchClearing: boolean;
  accessError: boolean;
}

export function useMyAssignedContactRequestsList(): UseMyAssignedContactRequestsListResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  // ---- Initial state from URL ----
  const initial = (() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trim();
    const urlStartDate = searchParams.get('startDate') ?? undefined;
    const urlEndDate = searchParams.get('endDate') ?? undefined;

    const pageNum = urlPage ? parseInt(urlPage, 10) : 1;
    const limitNum = urlLimit ? parseInt(urlLimit, 10) : 10;

    return {
      page: !isNaN(pageNum) && pageNum >= 1 ? pageNum : 1,
      limit: (ALLOWED_LIMITS as readonly number[]).includes(limitNum)
        ? limitNum
        : 10,
      search: urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch,
      startDate: urlStartDate,
      endDate: urlEndDate,
    };
  })();

  const {
    search,
    debouncedSearch,
    setSearch: setSearchWithLoad,
    setSearchDirect: setSearchState,
    loading,
    setLoading,
    effectiveLoading,
  } = useSearchLoading(initial.search, 500);

  const [items, setItems] = useState<ContactRequestListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [accessError, setAccessError] = useState(false);

  const [page, setPage] = useState(initial.page);
  const [limit, setLimit] = useState(initial.limit);
  const [limitError, setLimitError] = useState('');

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: initial.startDate,
    endDate: initial.endDate,
  });
  const [appliedFilters, setAppliedFilters] = useState<{
    dateRange: DateRange;
  }>({
    dateRange: { startDate: initial.startDate, endDate: initial.endDate },
  });

  const hasFetchedRef = useRef(false);
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  // -------------------------------------------------------------------------
  // URL → state (browser back/forward)
  // -------------------------------------------------------------------------
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trim();

    const nextPage = (() => {
      const v = urlPage ? parseInt(urlPage, 10) : 1;
      return !isNaN(v) && v >= 1 ? v : 1;
    })();
    if (nextPage !== page) {
      setPage(nextPage);
    }

    const nextLimit = (() => {
      const v = urlLimit ? parseInt(urlLimit, 10) : 10;
      return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
    })();
    if (nextLimit !== limit) {
      setLimit(nextLimit);
    }

    const nextSearch =
      urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch;
    if (nextSearch !== search) {
      setSearchState(nextSearch);
      if (nextSearch.trim() !== search.trim()) {
        setLoading(true);
      }
    }

    const nextStartDate = searchParams.get('startDate') ?? undefined;
    const nextEndDate = searchParams.get('endDate') ?? undefined;
    setDateRange((prev) => {
      if (prev.startDate === nextStartDate && prev.endDate === nextEndDate) {
        return prev;
      }
      return { startDate: nextStartDate, endDate: nextEndDate };
    });
    setAppliedFilters((prev) => {
      if (
        prev.dateRange.startDate === nextStartDate &&
        prev.dateRange.endDate === nextEndDate
      ) {
        return prev;
      }
      return { dateRange: { startDate: nextStartDate, endDate: nextEndDate } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset isSyncingFromUrl after state settles
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, appliedFilters]);

  // -------------------------------------------------------------------------
  // state → URL  (preserves params owned by other components: tab, cols)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }

    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', limit.toString());

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }

    if (appliedFilters.dateRange.startDate) {
      params.set('startDate', appliedFilters.dateRange.startDate);
    }
    if (appliedFilters.dateRange.endDate) {
      params.set('endDate', appliedFilters.dateRange.endDate);
    }

    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }
    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: false });
    }
  }, [page, limit, search, appliedFilters, searchParams, setSearchParams]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ dateRange });
    setPage(1);
  }, [dateRange]);

  const handleClearFilters = useCallback(() => {
    const hasAppliedFilters =
      !!appliedFilters.dateRange.startDate ||
      !!appliedFilters.dateRange.endDate;

    setDateRange({});

    if (hasAppliedFilters) {
      // Mark loading synchronously so `hasNoData` can't flip true in the render
      // between clearing the filters and the refetch actually starting —
      // otherwise the toolbar (and the open filter popup) would briefly unmount
      // and remount, causing a flicker.
      setLoading(true);
      setAppliedFilters({ dateRange: {} });
      setPage(1);
    }
  }, [appliedFilters, setLoading]);

  const setSearch = useCallback(
    (next: string) => {
      if (next.length > 200) {
        return;
      }
      setSearchWithLoad(next);
      setPage(1);
    },
    [setSearchWithLoad]
  );

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const trimmedSearch = debouncedSearch.trim();

      const response =
        await listMyAssignedContactRequestsApiService.listMyAssignedContactRequests(
          {
            page,
            limit,
            ...(trimmedSearch && { search: trimmedSearch }),
            ...(appliedFilters.dateRange.startDate && {
              startDate: appliedFilters.dateRange.startDate,
            }),
            ...(appliedFilters.dateRange.endDate && {
              endDate: appliedFilters.dateRange.endDate,
            }),
          }
        );

      const { totalCount: count, totalPages } = response.pagination;

      if (count > 0 && totalPages > 0 && page > totalPages) {
        setPage(totalPages);
        return;
      }

      setItems(response.data);
      setTotalCount(count);
    } catch (error: unknown) {
      if (
        typeof error === 'object' &&
        error !== null &&
        (error as { status?: number }).status === 403
      ) {
        setAccessError(true);
      } else {
        setItems([]);
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
      hasFetchedRef.current = true;
    }
  }, [page, limit, debouncedSearch, appliedFilters, setLoading]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const hasActiveFilters =
    !!appliedFilters.dateRange.startDate || !!appliedFilters.dateRange.endDate;

  const hasNoData =
    !loading &&
    hasFetchedRef.current &&
    page === 1 &&
    totalCount === 0 &&
    !debouncedSearch.trim() &&
    !hasActiveFilters;

  const isSearchClearing =
    !search.trim() && debouncedSearch.trim() !== '' && !hasActiveFilters;

  const justClearedSearch =
    prevDebouncedSearchRef.current.trim() !== '' &&
    debouncedSearch.trim() === '' &&
    !hasActiveFilters;

  return {
    items,
    loading: effectiveLoading,
    page,
    setPage,
    limit,
    totalCount,
    search,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    hasNoData,
    isSearchClearing: isSearchClearing || justClearedSearch,
    accessError,
  };
}
